// cash wallet block (integer cents) riding alongside the BMO balance
export interface CashBalance {
  available_cents: number
  withdrawable_cents: number
  currency: string
  status: string // active|frozen|closed
}

interface Me {
  email: string | null
  display_name?: string
  is_admin: boolean
  // customer-support capability (admins inherit it; support ≠ admin)
  is_support?: boolean
  player_id: number | null
  balance: number | null
  cash?: CashBalance | null
  needs_registration?: boolean
  // cash-dfs kyc gate state (none|pending|verified|failed|underage)
  kyc_status?: string
  // 18+ on the PLATFORM profile (cheerios BirthDate): false = under 18 → every
  // cash surface is hidden (CashAgeGate); true = 18+; null = unknown (no date on
  // the record / profile unreadable) → the verify wizard's own check gates
  cash_age_eligible?: boolean | null
  // active self-exclusion: play + deposits closed, withdrawals stay open;
  // ends_at null while excluded means indefinite
  self_excluded?: boolean
  self_exclusion_ends_at?: string | null
}

export function useAuth() {
  const user = useState<Me | null>('user', () => null)
  const api = useApi()
  const bridge = useNativeBridge()

  async function fetchMe() {
    try { user.value = await api.get<Me>('/me') } catch { user.value = null }
    return user.value
  }

  async function refreshBalance() {
    if (!user.value) return
    try {
      const b = await api.get<{ available_balance: number, cash?: CashBalance }>('/balance')
      // balance stays BMO points; cash rides alongside. The bridge still emits
      // ONLY the BMO number — v1 native shells know nothing about cash.
      user.value = { ...user.value, balance: b.available_balance, cash: b.cash ?? null }
      if (bridge.isNative.value) bridge.balanceChanged(b.available_balance)  // mirror into native chrome
    } catch { /* leave previous balance */ }
  }

  // Apply a balance pushed FROM native (app-side BMO change). Sets the displayed
  // value directly and does NOT emit balanceChanged — that would echo back to
  // native and risk a feedback loop. The source of truth stays the BMO wallet;
  // this just keeps ProBook's display in sync between authoritative fetches.
  function setBalance(available_balance: number) {
    if (!user.value || typeof available_balance !== 'number') return
    user.value = { ...user.value, balance: available_balance }
  }

  async function logout() {
    if (bridge.isNative.value) {
      // Clear our own session first: the WebView cookie jar outlives the bets
      // login, so a surviving probook_session would greet the device's NEXT bets
      // user as this one (BB-951). Then hand off to native — it owns the bets
      // session lifecycle in the embed (no /login bounce, which would desync).
      try { await api.post('/auth/logout') } catch {}
      user.value = null
      bridge.requestLogout()
      return
    }
    try { await api.post('/auth/logout') } catch {}
    user.value = null
    await navigateTo('/login')
  }

  return { user, fetchMe, refreshBalance, setBalance, logout }
}
